<template>
  <div class="study-viewer">
    <template v-if="study">
      <h2>
        {{ study.title }}
      </h2>
      <h4>{{ study.firstAuthor }} et al. {{ study.journal }}. {{ study.year }};{{ study.volume }}.</h4>
      <template v-if="study.conclusion">
        <h3>
          {{ study.conclusion.label }}
        </h3>
        <p>
          {{ study.conclusion.value }}
        </p>
      </template>
      <template v-if="rct">
        <template v-if="rct.overallRiskOfBiasJudgement">
          <h3>
            {{ rct.overallRiskOfBiasJudgement.label }}
          </h3>
          <p>
            {{ rct.overallRiskOfBiasJudgement.value }}
          </p>
        </template>
        <template v-if="rct.funding">
          <h3>
            {{ rct.funding.label }}
          </h3>
          <p>
            {{ rct.funding.value }}
          </p>
        </template>
      </template>
      <h5 class="clickable" @click="e => copyCiteToClipboard()">
        <i class="fas fa-quote-right"/> Cite <i class="fas fa-quote-right"/><i>{{ copied }}</i>
      </h5>
      <h5>
        <a v-if="rct?.xlsx" :href="study.xlsx" target="_blank"><i class="fas fa-file"/> Processed data</a>
      </h5>
      <h5>
        <a :href="study.link" target="_blank"><i class="fas fa-link"/> Article link </a>

      </h5>
      <ShareButtons :text="study.share.replaceAll('{%content%}', `${study.firstAuthor} et al. ${study.title}`)"/>
    </template>
    <template v-else-if="prot">

      <h2>{{ prot.title }}</h2>
      <h4>{{ prot.identifier.value }}</h4>
      <h3>{{ prot.expectedEndOfTrial.label }}</h3>
      <p>{{ prot.expectedEndOfTrial.value }}</p>
      <h5>
        <a :href="prot.link" target="_blank"><i class="fas fa-link"/> Link</a>
      </h5>
    </template>
    <template v-else-if="defGuide">
      <h2>{{ defGuide.titleArticle }}</h2>
      <h4>{{ defGuide.firstAuthor }} et al. {{ defGuide.journal }}. {{ defGuide.year }};{{ defGuide.volume }}.</h4>
      <template v-if="def">
        <h3>
          {{ def.definition.label }}
        </h3>
        <p>
          {{ def.definition.value }}
        </p>
      </template>
      <template v-else>

        <h3>
          {{ guide.guideline.label }}
        </h3>
        <p>
          {{ guide.guideline.value }}
        </p>
      </template>
      <h5 class="clickable" @click="e => copyCiteToClipboard()">
        <i class="fas fa-quote-right"/> Cite <i class="fas fa-quote-right"/><i>{{ copied }}</i>
      </h5>
      <h5>
        <a :href="defGuide.link" target="_blank"><i class="fas fa-link"/> Source </a>
      </h5>
      <ShareButtons
          :text="defGuide.share.replaceAll('{%content%}', `${defGuide.firstAuthor} et al. ${defGuide.titleShown}`)"/>
    </template>
    <template v-else-if="map">
      <h2>{{ map.title }}</h2>
      <h4 class="map-subtitle">{{ map.date }}</h4>
      <div class="map-info-container">
        <h5><a target="_blank" :href="map.flowPath">Flowchart</a></h5>
        <h5><a target="_blank" :href="map.summaryPath">Summary</a></h5>
        <h5><a target="_blank" :href="mapStore.state.instructionsMap">Instructions MAP display</a></h5>
        <h5><a target="_blank" :href="mapStore.state.instructionsTabular">Instructions TABULAR display</a></h5>
      </div>
      <h5 class="clickable mt-1" @click="e => copyCiteToClipboard()">
        <i class="fas fa-quote-right"/> Cite <i class="fas fa-quote-right"/><i>{{ copied }}</i>
      </h5>
      <h5>
        <a :href="map.link" target="_blank"><i class="fas fa-link"/> Article link </a>
      </h5>
      <h3>About</h3>
      {{ mapStore.state.about }}
    </template>


  </div>
</template>
<script lang="ts">
import {defineComponent, PropType, ref} from "vue";
import {
  IDefGuideBase,
  IDefinition,
  IEvidenceMap,
  IGuideline,
  IProt,
  IRct,
  ISr,
  IStudy
} from "@/interfaces/evidence-map";
import ShareButtons from "@/components/ShareButtons.vue";
import {useMapStore} from "@/mixins/mapStore";

export default defineComponent({
  name: "DataViewer",
  components: {ShareButtons},
  props: {
    rct: {
      type: Object as PropType<IRct>
    },
    sr: {
      type: Object as PropType<ISr>
    },
    prot: {
      type: Object as PropType<IProt>
    },
    def: {
      type: Object as PropType<IDefinition>
    },
    guide: {
      type: Object as PropType<IGuideline>
    },
    map: {
      type: Object as PropType<IEvidenceMap>
    }
  },
  setup(props) {
    const mapStore = useMapStore()
    const study = ref<IStudy | null>(null)
    const defGuide = ref<IDefGuideBase>(null)
    if (props.rct) {
      study.value = Object.assign(props.rct);
    } else if (props.sr) {
      study.value = Object.assign(props.sr);
    } else if (props.def) {
      defGuide.value = Object.assign(props.def)
    } else if (props.guide) {
      defGuide.value = Object.assign(props.guide)
    }

    const copied = ref<string>("")

    const copyCiteToClipboard = () => {
      const l_Cite = props.map ? props.map.citation :study.value ?
          `${study.value.firstAuthor} et al. ${study.value.title}. ${study.value.journal}. ${study.value.year}; ${study.value.volume}.` :
          `${defGuide.value.firstAuthor} et al. ${defGuide.value.titleShown}. ${defGuide.value.journal}. ${defGuide.value.year}; ${defGuide.value.volume}.`
      navigator.clipboard.writeText(l_Cite).then(function () {
        copied.value = "copied!"
        setTimeout(() => {
          copied.value = ""
        }, 1000)
      }, function (err) {
        console.error('Async: Could not copy text: ', err);
      });
    }
    return {
      copyCiteToClipboard,
      copied,
      study,
      defGuide,
      mapStore,
    }


  }
})
</script>
<style lang="scss">
@import "src/assets/styles/vars";

.study-viewer {

  width: 25vw;
  height: 100%;
  padding: 4vh $default-padding;

  h3 {
    margin-top: 4vh;
  }
  h4.map-subtitle {
    margin-bottom: 6vh;
  }

  h5:first-child {
    margin-top: 2vh;
  }

  h5 {
    margin-bottom: 0.6vh;

    i {
      font-style: italic;
      margin-right: 0.3vw;
    }
  }
  .map-info-container {
    margin-bottom: 3vh;
  }
}
</style>